import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import styled from 'styled-components';
import logo from '../assets/images/logo.png';

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: rgba(18, 25, 16, 0.9);
  backdrop-filter: blur(5px);
  padding: ${props => props.scrolled ? '10px 0' : '15px 0'};
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(188, 179, 113, 0.2);
  box-shadow: ${props => props.scrolled ? '0 5px 15px rgba(0, 0, 0, 0.3)' : 'none'};
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavLogo = styled(Link)`
  display: block;
  
  img {
    height: ${props => props.scrolled ? '40px' : '50px'};
    transition: height 0.3s ease;
  }
`;

const NavMenu = styled.div`
  display: flex;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    width: 80%;
    max-width: 400px;
    height: 100%;
    background-color: var(--dark-color);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transition: 0.5s;
    z-index: 100;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.3);
  }
`;

const NavLinks = styled.ul`
  display: flex;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const NavItem = styled.li`
  margin-left: 30px;
  
  @media (max-width: 768px) {
    margin: 20px 0;
  }
`;

const NavLink = styled(Link)`
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.9rem;
  letter-spacing: 1px;
  padding: 5px 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--secondary-color);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ScrollNavLink = styled(ScrollLink)`
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.9rem;
  letter-spacing: 1px;
  padding: 5px 0;
  position: relative;
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--secondary-color);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Hamburger = styled.div`
  display: none;
  cursor: pointer;
  z-index: 101;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Bar = styled.div`
  display: block;
  width: 25px;
  height: 3px;
  margin: 5px auto;
  transition: all 0.3s ease;
  background-color: var(--text-color);
  
  &:nth-child(1) {
    transform: ${props => props.isOpen ? 'translateY(8px) rotate(45deg)' : 'none'};
  }
  
  &:nth-child(2) {
    opacity: ${props => props.isOpen ? '0' : '1'};
  }
  
  &:nth-child(3) {
    transform: ${props => props.isOpen ? 'translateY(-8px) rotate(-45deg)' : 'none'};
  }
`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHomePage, setIsHomePage] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Check if we're on the home page
    setIsHomePage(window.location.pathname === '/');

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <NavbarContainer scrolled={scrolled}>
      <div className="container">
        <NavContent>
          <NavLogo to="/" scrolled={scrolled}>
            <img src={logo} alt="Dev Team Six Logo" />
          </NavLogo>
          <NavMenu isOpen={isMenuOpen}>
            <NavLinks>
              {isHomePage ? (
                <>
                  <NavItem>
                    <ScrollNavLink 
                      to="sobre" 
                      smooth={true} 
                      duration={500} 
                      offset={-80}
                      onClick={closeMenu}
                    >
                      Sobre
                    </ScrollNavLink>
                  </NavItem>
                  <NavItem>
                    <ScrollNavLink 
                      to="missao" 
                      smooth={true} 
                      duration={500} 
                      offset={-80}
                      onClick={closeMenu}
                    >
                      Missão
                    </ScrollNavLink>
                  </NavItem>
                  <NavItem>
                    <ScrollNavLink 
                      to="valores" 
                      smooth={true} 
                      duration={500} 
                      offset={-80}
                      onClick={closeMenu}
                    >
                      Valores
                    </ScrollNavLink>
                  </NavItem>
                  <NavItem>
                    <ScrollNavLink 
                      to="time" 
                      smooth={true} 
                      duration={500} 
                      offset={-80}
                      onClick={closeMenu}
                    >
                      Time
                    </ScrollNavLink>
                  </NavItem>
                </>
              ) : (
                <>
                  <NavItem>
                    <NavLink to="/" onClick={closeMenu}>Home</NavLink>
                  </NavItem>
                </>
              )}
              <NavItem>
                <NavLink to="/projects" onClick={closeMenu}>Projetos</NavLink>
              </NavItem>
              <NavItem>
                <NavLink to="/contact" onClick={closeMenu}>Contato</NavLink>
              </NavItem>
            </NavLinks>
          </NavMenu>
          <Hamburger onClick={toggleMenu} isOpen={isMenuOpen}>
            <Bar isOpen={isMenuOpen} />
            <Bar isOpen={isMenuOpen} />
            <Bar isOpen={isMenuOpen} />
          </Hamburger>
        </NavContent>
      </div>
    </NavbarContainer>
  );
};

export default Navbar;
